import { firebaseConfig, firebaseVapidKey, pushConfigured } from "../config"

/**
 * Desktop notifications.
 *
 * Two mechanisms, and they cover different moments:
 *
 *   IN-TAB    `new Notification(...)`, fired by the app when a socket event
 *             arrives. Works with no Firebase, no service worker and no server
 *             key — but only while the page is alive.
 *
 *   BACKGROUND  FCM through a service worker. This is the one that arrives when
 *             the tab is closed, which is most of the time and exactly when a
 *             notification is worth having.
 *
 * The app uses in-tab as the baseline and layers FCM on when it is configured, so
 * a deployment without Firebase still gets desktop popups rather than an "enable
 * notifications" button that cannot do anything.
 */

let messagingPromise = null

/** 'granted' | 'denied' | 'default' | 'unsupported' */
export function notificationPermission() {
  if (typeof Notification === "undefined") return "unsupported"
  return Notification.permission
}

/**
 * Ask the browser for permission.
 *
 * Call this from a click. Browsers increasingly refuse — or permanently block —
 * a permission prompt that was not triggered by a user gesture, and a denial is
 * sticky: the user has to go into site settings to undo it. Prompting on page
 * load is the fastest way to lose the permission forever.
 */
export async function requestNotificationPermission() {
  if (typeof Notification === "undefined") return "unsupported"
  if (Notification.permission !== "default") return Notification.permission
  try {
    const result = await Notification.requestPermission()
    /*
     * Still "default" after asking means the browser swallowed the prompt.
     *
     * Edge ships "quiet notification requests" switched ON, and Chrome and
     * Firefox turn something equivalent on for people who habitually decline.
     * The request resolves normally and nothing appears — the browser puts a
     * small bell or a crossed-out bell in the address bar instead, which nobody
     * is looking at because they just clicked a button in the page.
     *
     * Reported as its own outcome, not as a denial: nothing was refused, and
     * telling somebody they blocked notifications when they did not sends them
     * to the wrong setting.
     */
    return result === "default" ? "suppressed" : result
  } catch {
    return "denied"
  }
}

/**
 * What to tell somebody whose browser suppressed the prompt.
 *
 * One sentence, in one place, because the banner and the settings page have to
 * say the same thing — and it has to name the thing they can actually see
 * rather than a menu path that is different in every browser.
 */
export const SUPPRESSED_PROMPT_HELP =
  "Your browser held the request back rather than asking — look for a bell icon at the right-hand end of the address bar and choose Allow. In Edge this is the \u201cQuiet notification requests\u201d setting, under Settings \u2192 Cookies and site permissions \u2192 Notifications."

/** Show a desktop notification from the page. Returns false if it could not. */
export function showDesktopNotification({ title, body, tag, onClick }) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return false
  // Not while the tab is focused: the in-app popup is already showing it, and two
  // notifications for one event reads as a bug.
  if (typeof document !== "undefined" && document.visibilityState === "visible") return false

  try {
    const notification = new Notification(title, {
      body,
      icon: "/favicon.svg",
      // Same tag collapses repeats, so ten messages in one thread are one
      // notification rather than ten stacked.
      tag: tag || "tallywise",
      renotify: Boolean(tag),
    })
    notification.onclick = () => {
      window.focus()
      notification.close()
      if (onClick) onClick()
    }
    return true
  } catch {
    return false
  }
}

/**
 * Register for background push and return the FCM token.
 *
 * Everything is loaded lazily: `firebase/messaging` is a large dependency and a
 * deployment without push should not pay for it on first render.
 *
 * @returns {Promise<string|null>} the token, or null when push is unavailable
 */
export async function registerForPush() {
  if (!pushConfigured) return null
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null
  if (notificationPermission() !== "granted") return null

  try {
    if (!messagingPromise) {
      messagingPromise = (async () => {
        const { initializeApp, getApps } = await import("firebase/app")
        const { getMessaging, isSupported } = await import("firebase/messaging")
        // Safari and some embedded browsers have no FCM support at all. Asking
        // first turns a thrown error into a clean "push is unavailable here".
        if (!(await isSupported())) return null
        const app = getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfig)
        return getMessaging(app)
      })()
    }

    const messaging = await messagingPromise
    if (!messaging) return null

    const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js")
    // The worker cannot read the app's build-time environment, so the config is
    // posted through. `ready` first: a freshly registered worker is not yet
    // controlling the page and would never receive the message.
    await navigator.serviceWorker.ready
    registration.active?.postMessage({ type: "FIREBASE_CONFIG", config: firebaseConfig })

    const { getToken } = await import("firebase/messaging")
    return await getToken(messaging, {
      vapidKey: firebaseVapidKey,
      serviceWorkerRegistration: registration,
    })
  } catch (err) {
    // Push is an enhancement. A failure here must never break the app.
    console.warn("[push] registration failed:", err?.message || err)
    return null
  }
}

/** Foreground FCM messages, for the rare case where the socket is down but push is up. */
export async function onForegroundPush(handler) {
  if (!pushConfigured || !messagingPromise) return () => {}
  try {
    const messaging = await messagingPromise
    if (!messaging) return () => {}
    const { onMessage } = await import("firebase/messaging")
    return onMessage(messaging, handler)
  } catch {
    return () => {}
  }
}
